/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Comprehensive Responsive Hook
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * Provides device detection, breakpoint awareness, and responsive utilities
 * for all device types: phones, tablets, laptops, desktops, TVs
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

// Breakpoint values matching tailwind.config.js
export const BREAKPOINTS = {
  xs: 320,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1920,
  '4xl': 2560,
};

// Device type thresholds
const DEVICE_THRESHOLDS = {
  phone: { maxWidth: 639 },
  tablet: { minWidth: 640, maxWidth: 1023 },
  laptop: { minWidth: 1024, maxWidth: 1535 },
  desktop: { minWidth: 1536, maxWidth: 1919 },
  tv: { minWidth: 1920 },
};

/**
 * Comprehensive responsive hook for device-aware UI
 * @returns {Object} Responsive state and utilities
 */
export const useResponsive = () => {
  const [state, setState] = useState(() => getResponsiveState());

  // Debounced resize handler
  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setState(getResponsiveState());
      }, 100); // 100ms debounce
    };

    // Also listen for orientation changes
    const handleOrientationChange = () => {
      setState(getResponsiveState());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Media query listeners for special states
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleMotionChange = () => setState(prev => ({ ...prev, prefersReducedMotion: reducedMotionQuery.matches }));
    const handleDarkModeChange = () => setState(prev => ({ ...prev, prefersDarkMode: darkModeQuery.matches }));
    
    reducedMotionQuery.addEventListener('change', handleMotionChange);
    darkModeQuery.addEventListener('change', handleDarkModeChange);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      reducedMotionQuery.removeEventListener('change', handleMotionChange);
      darkModeQuery.removeEventListener('change', handleDarkModeChange);
    };
  }, []);

  return state;
};

/**
 * Get current responsive state
 */
function getResponsiveState() {
  if (typeof window === 'undefined') {
    return getDefaultState();
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  const orientation = width > height ? 'landscape' : 'portrait';
  const aspectRatio = width / height;
  
  // Device type detection
  const deviceType = getDeviceType(width);
  
  // Breakpoint flags
  const breakpoint = getCurrentBreakpoint(width);
  
  // Touch capability detection
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const hasMouse = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const hasStylus = window.matchMedia('(hover: none) and (pointer: fine)').matches;
  
  // Safe area insets (for notched phones)
  const safeAreaInsets = getSafeAreaInsets();
  
  // User preferences
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  return {
    // Dimensions
    width,
    height,
    orientation,
    aspectRatio,
    
    // Device type
    deviceType,
    isPhone: deviceType === 'phone',
    isTablet: deviceType === 'tablet',
    isLaptop: deviceType === 'laptop',
    isDesktop: deviceType === 'desktop',
    isTV: deviceType === 'tv',
    isMobile: deviceType === 'phone' || deviceType === 'tablet',
    isLargeScreen: deviceType === 'desktop' || deviceType === 'tv',
    
    // Current breakpoint
    breakpoint,
    
    // Breakpoint checks (is at least this size)
    isXs: width >= BREAKPOINTS.xs,
    isSm: width >= BREAKPOINTS.sm,
    isMd: width >= BREAKPOINTS.md,
    isLg: width >= BREAKPOINTS.lg,
    isXl: width >= BREAKPOINTS.xl,
    is2xl: width >= BREAKPOINTS['2xl'],
    is3xl: width >= BREAKPOINTS['3xl'],
    is4xl: width >= BREAKPOINTS['4xl'],
    
    // Orientation
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',
    isShortViewport: height < 600,
    isTallViewport: height >= 900,
    
    // Input capabilities
    hasTouch,
    hasMouse,
    hasStylus,
    inputType: hasStylus ? 'stylus' : hasTouch ? 'touch' : 'mouse',
    
    // Safe areas
    safeAreaInsets,
    hasNotch: safeAreaInsets.top > 20,
    
    // User preferences
    prefersReducedMotion,
    prefersDarkMode,
    
    // Utility values for common responsive patterns
    containerPadding: getContainerPadding(width),
    modalWidth: getModalWidth(width, deviceType),
    fontSize: getFontSizeScale(width),
    spacing: getSpacingScale(width),
  };
}

/**
 * Get default state for SSR
 */
function getDefaultState() {
  return {
    width: 1280,
    height: 800,
    orientation: 'landscape',
    aspectRatio: 1.6,
    deviceType: 'laptop',
    isPhone: false,
    isTablet: false,
    isLaptop: true,
    isDesktop: false,
    isTV: false,
    isMobile: false,
    isLargeScreen: false,
    breakpoint: 'xl',
    isXs: true,
    isSm: true,
    isMd: true,
    isLg: true,
    isXl: true,
    is2xl: false,
    is3xl: false,
    is4xl: false,
    isPortrait: false,
    isLandscape: true,
    isShortViewport: false,
    isTallViewport: false,
    hasTouch: false,
    hasMouse: true,
    hasStylus: false,
    inputType: 'mouse',
    safeAreaInsets: { top: 0, right: 0, bottom: 0, left: 0 },
    hasNotch: false,
    prefersReducedMotion: false,
    prefersDarkMode: false,
    containerPadding: 24,
    modalWidth: 480,
    fontSize: 1,
    spacing: 1,
  };
}

/**
 * Determine device type based on width
 */
function getDeviceType(width) {
  if (width < DEVICE_THRESHOLDS.phone.maxWidth + 1) return 'phone';
  if (width <= DEVICE_THRESHOLDS.tablet.maxWidth) return 'tablet';
  if (width <= DEVICE_THRESHOLDS.laptop.maxWidth) return 'laptop';
  if (width <= DEVICE_THRESHOLDS.desktop.maxWidth) return 'desktop';
  return 'tv';
}

/**
 * Get current breakpoint name
 */
function getCurrentBreakpoint(width) {
  if (width >= BREAKPOINTS['4xl']) return '4xl';
  if (width >= BREAKPOINTS['3xl']) return '3xl';
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

/**
 * Get safe area insets for notched devices
 */
function getSafeAreaInsets() {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  
  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('--sat') || style.getPropertyValue('env(safe-area-inset-top)') || '0', 10),
    right: parseInt(style.getPropertyValue('--sar') || style.getPropertyValue('env(safe-area-inset-right)') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--sab') || style.getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
    left: parseInt(style.getPropertyValue('--sal') || style.getPropertyValue('env(safe-area-inset-left)') || '0', 10),
  };
}

/**
 * Get container padding based on screen size
 */
function getContainerPadding(width) {
  if (width < BREAKPOINTS.sm) return 12;  // xs: 12px
  if (width < BREAKPOINTS.md) return 16;  // sm: 16px
  if (width < BREAKPOINTS.lg) return 20;  // md: 20px
  if (width < BREAKPOINTS.xl) return 24;  // lg: 24px
  return 32;                               // xl+: 32px
}

/**
 * Get modal width based on device type
 */
function getModalWidth(width, deviceType) {
  switch (deviceType) {
    case 'phone':
      return Math.min(width - 24, 400);  // Full width minus padding
    case 'tablet':
      return Math.min(width - 48, 560);
    case 'laptop':
      return 480;
    case 'desktop':
      return 520;
    case 'tv':
      return 640;
    default:
      return 480;
  }
}

/**
 * Get font size scale multiplier
 */
function getFontSizeScale(width) {
  if (width < BREAKPOINTS.sm) return 0.875;   // xs: slightly smaller
  if (width < BREAKPOINTS.lg) return 1;       // sm-md: base
  if (width < BREAKPOINTS['3xl']) return 1;   // lg-2xl: base
  return 1.125;                                // 3xl+: slightly larger for TV
}

/**
 * Get spacing scale multiplier
 */
function getSpacingScale(width) {
  if (width < BREAKPOINTS.sm) return 0.75;    // xs: tighter spacing
  if (width < BREAKPOINTS.lg) return 1;       // sm-md: base
  if (width >= BREAKPOINTS['3xl']) return 1.5; // 3xl+: more spacious for TV
  return 1;
}

/**
 * Hook for responsive class generation
 * @param {Object} classes - Object mapping breakpoints to class strings
 * @returns {string} Combined class string for current breakpoint
 * 
 * Usage:
 * const className = useResponsiveClass({
 *   base: 'p-2 text-sm',
 *   sm: 'p-3 text-base',
 *   lg: 'p-4 text-lg',
 * });
 */
export const useResponsiveClass = (classes) => {
  const { breakpoint } = useResponsive();
  
  return useMemo(() => {
    const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'];
    const currentIndex = breakpointOrder.indexOf(breakpoint);
    
    let result = classes.base || '';
    
    for (let i = 0; i <= currentIndex; i++) {
      const bp = breakpointOrder[i];
      if (classes[bp]) {
        result = `${result} ${classes[bp]}`;
      }
    }
    
    return result.trim();
  }, [breakpoint, classes]);
};

/**
 * Hook for responsive value selection
 * @param {Object} values - Object mapping breakpoints to values
 * @returns {*} Value for current breakpoint
 * 
 * Usage:
 * const columns = useResponsiveValue({
 *   base: 1,
 *   sm: 2,
 *   lg: 3,
 *   xl: 4,
 * });
 */
export const useResponsiveValue = (values) => {
  const { breakpoint } = useResponsive();
  
  return useMemo(() => {
    const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'];
    const currentIndex = breakpointOrder.indexOf(breakpoint);
    
    let result = values.base;
    
    for (let i = 0; i <= currentIndex; i++) {
      const bp = breakpointOrder[i];
      if (values[bp] !== undefined) {
        result = values[bp];
      }
    }
    
    return result;
  }, [breakpoint, values]);
};

/**
 * Responsive configuration presets for common UI patterns
 */
export const RESPONSIVE_PRESETS = {
  // Modal/Dialog sizing
  modal: {
    xs: { width: 'calc(100vw - 24px)', maxHeight: 'calc(100vh - 48px)' },
    sm: { width: '400px', maxHeight: 'calc(100vh - 64px)' },
    md: { width: '480px', maxHeight: 'calc(100vh - 80px)' },
    lg: { width: '520px', maxHeight: 'calc(100vh - 96px)' },
    xl: { width: '560px', maxHeight: 'calc(100vh - 96px)' },
    '3xl': { width: '640px', maxHeight: 'calc(100vh - 120px)' },
  },
  
  // Panel sizing (sidebars, drawers)
  panel: {
    xs: { width: '100vw' },
    sm: { width: '320px' },
    md: { width: '360px' },
    lg: { width: '400px' },
    xl: { width: '440px' },
    '3xl': { width: '520px' },
  },
  
  // Card grid columns
  cardGrid: {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 3,
    xl: 4,
    '3xl': 5,
    '4xl': 6,
  },
  
  // Font size scales
  heading: {
    xs: 'text-xl',
    sm: 'text-2xl',
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-3xl',
    '3xl': 'text-4xl',
  },
  
  // Touch target sizes (minimum 44px for accessibility)
  touchTarget: {
    xs: 44,
    sm: 44,
    md: 40,
    lg: 36,
    xl: 36,
    '3xl': 48,  // Larger for TV remote navigation
  },
};

export default useResponsive;
